import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, ChevronUp, Mail, Image, Video, FileText, Music, Edit, Trash, Plus, X, Search, Check, Share2 } from 'lucide-react';
import { DashboardApi } from '../../../apis/DashboardApi';
import { ToastContainer, toast } from 'react-toastify';

interface GroupMedia {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
}

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
    is_legacy: number;
    created_at?: string;
    file_size?: string;
    recipients?: any;
    recipientTemporaryUrl?: any;
    handleDelete?: () => void;
    handleRename?: () => void;
    handleShare?: () => void;
}

interface GroupMember {
    id: number;
    email: string;
    user_id?: number;
}

interface Recipient {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    temporaryUrl?: string;
    group?: {
        id: number;
        name: string;
    };
    contact?: {
        id: number;
        name: string;
    };
}

interface GroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    media: Media[];
}

const GroupSharingModal: React.FC<GroupModalProps> = ({ isOpen, onClose, media }) => {
    const [groupName, setGroupName] = useState('');
    const [selectedMedia, setSelectedMedia] = useState<GroupMedia[]>([]);
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // New states for recipient selection
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
    const [recipientSearchTerm, setRecipientSearchTerm] = useState('');
    const [showRecipientSelector, setShowRecipientSelector] = useState(false);
    const [loadingRecipients, setLoadingRecipients] = useState(false);

    // Fetch recipients when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchRecipients();
        }
    }, [isOpen]);

    const fetchRecipients = async () => {
        setLoadingRecipients(true);
        try {
            const response: any = await DashboardApi.getRecipient();
            if (response.data?.status === 'success') {
                setRecipients(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching recipients:', error);
            toast.error('Failed to fetch recipients');
        } finally {
            setLoadingRecipients(false);
        }
    };

    const handleAddMember = (e: React.FormEvent) => {
        e.preventDefault();
        if (newEmail && !members.find(m => m.email === newEmail)) {
            setMembers([...members, { id: Date.now(), email: newEmail }]);
            setNewEmail('');
        }
    };

    const handleRemoveMember = (email: string) => {
        setMembers(members.filter(m => m.email !== email));
    };

    const handleToggleRecipient = (recipient: Recipient) => {
        const isSelected = selectedRecipients.find(r => r.id === recipient.id);
        if (isSelected) {
            setSelectedRecipients(selectedRecipients.filter(r => r.id !== recipient.id));
            // Also remove from members list
            setMembers(members.filter(m => m.email !== recipient.email));
        } else {
            setSelectedRecipients([...selectedRecipients, recipient]);
            // Add to members list if not already present
            if (!members.find(m => m.email === recipient.email)) {
                setMembers([...members, {
                    id: recipient.id,
                    email: recipient.email,
                    user_id: recipient.id
                }]);
            }
        }
    };

    const handleToggleMedia = (mediaItem: GroupMedia) => {
        const isSelected = selectedMedia.find(m => m.id === mediaItem.id);
        if (isSelected) {
            setSelectedMedia(selectedMedia.filter(m => m.id !== mediaItem.id));
        } else {
            setSelectedMedia([...selectedMedia, mediaItem]);
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName) {
            setError('Group name is required');
            return;
        }
        if (members.length === 0) {
            setError('Add at least one member');
            return;
        }
        if (selectedMedia.length === 0) {
            setError('Select at least one media file');
            return;
        }

        setLoading(true);
        try {
            const response: any = await DashboardApi.createGroup({
                name: groupName,
                emails: members.map(m => m.email),
                media_ids: selectedMedia.map(m => m.id)
            });

            if (response.data?.status) {
                toast.success('Group created successfully');
                onClose();
                window.location.reload();
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to create group');
        } finally {
            setLoading(false);
        }
    };

    const filteredMedia = media.filter(m =>
        m.file_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredRecipients = recipients.filter(r =>
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(recipientSearchTerm.toLowerCase()) ||
        r.email.toLowerCase().includes(recipientSearchTerm.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">Create Sharing Group</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Group Name Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Group Name
                        </label>
                        <input
                            type="text"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                            placeholder="Enter group name"
                        />
                    </div>

                    {/* Members Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Add Members
                        </label>

                        {/* Manual Email Input */}
                        <form onSubmit={handleAddMember} className="flex gap-2 mb-4">
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                className="flex-1 p-2 border rounded-lg"
                                placeholder="Enter email address"
                            />
                            <button
                                type="submit"
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </form>

                        {/* Recipient Selection Toggle */}
                        <div className="mb-4">
                            <button
                                onClick={() => setShowRecipientSelector(!showRecipientSelector)}
                                className="flex items-center gap-2 text-blue-500 hover:text-blue-600 text-sm"
                            >
                                <Users className="w-4 h-4" />
                                {showRecipientSelector ? 'Hide Recipients' : 'Select from Recipients'}
                                {showRecipientSelector ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                        </div>

                        {/* Recipient Selector */}
                        {showRecipientSelector && (
                            <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                                <div className="flex gap-2 mb-3">
                                    <Search className="w-5 h-5 text-gray-400 mt-2" />
                                    <input
                                        type="text"
                                        value={recipientSearchTerm}
                                        onChange={(e) => setRecipientSearchTerm(e.target.value)}
                                        className="flex-1 p-2 border rounded-lg"
                                        placeholder="Search recipients..."
                                    />
                                </div>

                                {loadingRecipients ? (
                                    <div className="text-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                    </div>
                                ) : (
                                    <div className="max-h-48 overflow-y-auto space-y-2">
                                        {filteredRecipients.map((recipient) => {
                                            const isSelected = selectedRecipients.find(r => r.id === recipient.id);
                                            return (
                                                <div
                                                    key={recipient.id}
                                                    onClick={() => handleToggleRecipient(recipient)}
                                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected
                                                            ? 'bg-blue-100 border-blue-300 border'
                                                            : 'bg-white hover:bg-gray-100 border border-gray-200'
                                                        }`}
                                                >
                                                    {recipient.temporaryUrl && (
                                                        <img
                                                            src={recipient.temporaryUrl}
                                                            alt={`${recipient.first_name} ${recipient.last_name}`}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                    )}
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">
                                                                {recipient.first_name} {recipient.last_name}
                                                            </span>
                                                            {isSelected && (
                                                                <Check className="w-4 h-4 text-blue-500" />
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-500">{recipient.email}</p>
                                                        {recipient.group && (
                                                            <p className="text-xs text-gray-400">
                                                                Group: {recipient.group.name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {filteredRecipients.length === 0 && (
                                            <p className="text-gray-500 text-center py-4">
                                                No recipients found
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Selected Members List */}
                        <div className="space-y-2">
                            {members.map((member) => {
                                const recipient = recipients.find(r => r.email === member.email);
                                return (
                                    <div key={member.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                        <div className="flex items-center gap-2">
                                            {recipient ? (
                                                <>
                                                    {recipient.temporaryUrl && (
                                                        <img
                                                            src={recipient.temporaryUrl}
                                                            alt={`${recipient.first_name} ${recipient.last_name}`}
                                                            className="w-6 h-6 rounded-full object-cover"
                                                        />
                                                    )}
                                                    <div>
                                                        <span className="text-sm font-medium">
                                                            {recipient.first_name} {recipient.last_name}
                                                        </span>
                                                        <p className="text-xs text-gray-500">{member.email}</p>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <Mail className="w-4 h-4 text-gray-500" />
                                                    <span>{member.email}</span>
                                                </>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => {
                                                handleRemoveMember(member.email);
                                                // Also remove from selected recipients if it exists
                                                if (recipient) {
                                                    setSelectedRecipients(selectedRecipients.filter(r => r.id !== recipient.id));
                                                }
                                            }}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Media Selection Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Media
                        </label>
                        <div className="flex gap-2 mb-4">
                            <Search className="w-5 h-5 text-gray-400 mt-2" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="flex-1 p-2 border rounded-lg"
                                placeholder="Search media files"
                            />
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {filteredMedia.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => handleToggleMedia(item)}
                                    className={`relative rounded-lg overflow-hidden cursor-pointer border-2 
                                        ${selectedMedia.find(m => m.id === item.id)
                                            ? 'border-blue-500'
                                            : 'border-transparent'}`}
                                >
                                    {item.file_type === 'image' && (
                                        <img
                                            src={item.temporaryUrl}
                                            alt={item.file_name}
                                            className="w-full h-32 object-cover"
                                        />
                                    )}

                                    {item.file_type === 'video' && (
                                        <div className="relative w-full h-32 bg-gray-100">
                                            <video
                                                src={item.temporaryUrl}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <Video className="w-8 h-8 text-gray-500" />
                                            </div>
                                        </div>
                                    )}

                                    {item.file_type === 'audio' && (
                                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                            <Music className="w-8 h-8 text-gray-500" />
                                        </div>
                                    )}

                                    {item.file_type === 'document' && (
                                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                            <FileText className="w-8 h-8 text-gray-500" />
                                        </div>
                                    )}

                                    <div className="p-2 bg-gray-50">
                                        <p className="text-sm truncate">{item.file_name}</p>
                                    </div>

                                    {selectedMedia.find(m => m.id === item.id) && (
                                        <div className="absolute top-2 right-2">
                                            <div className="bg-blue-500 rounded-full p-1">
                                                <Check className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {error && (
                        <p className="text-red-500 text-sm">{error}</p>
                    )}

                    <div className="flex justify-end gap-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateGroup}
                            disabled={loading}
                            className="px-6 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Create Group'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GroupSharingModal;